'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

interface Printer {
  id: string;
  name: string;
  model: string;
  category: string;
  price: number;
  original_price: number | null;
  image_url: string;
  description: string;
  features: string[];
  specs: Record<string, string>;
  in_stock: boolean;
  badge: string | null;
}

interface CartItem {
  printer: Printer;
  quantity: number;
}

const CATEGORIES = ['All', 'DeskJet', 'LaserJet', 'Envy', 'OfficeJet'];

const FALLBACK_IMAGE = 'https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,h=513,fit=crop/A8penpWCxOJMDyqC/vecteezy_a-dark-gray-printer-with-blank-paper-ready-to-print-a-document_71274071-NKk27j17yVWVLUo7.png';

// HP printer product images — distinct per model using reliable Unsplash printer photos
const HP_PRINTER_IMAGES: Record<string, string> = {
  'DeskJet 2755e': 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=400&h=300&fit=crop&q=80',
  'OfficeJet Pro 9015e': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&q=80',
  'LaserJet Pro M404dn': 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=400&h=300&fit=crop&q=80',
  'ENVY 6055e': 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=400&h=300&fit=crop&q=80',
  'Color LaserJet Pro M255dw': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&q=80',
  'OfficeJet 250': 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=400&h=300&fit=crop&q=80',
};

function getPrinterImage(printer: Printer): string {
  for (const [key, url] of Object.entries(HP_PRINTER_IMAGES)) {
    if (printer.model.includes(key) || printer.name.includes(key)) return url;
  }
  return FALLBACK_IMAGE;
}

export default function BuyPage() {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedPrinter, setSelectedPrinter] = useState<Printer | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [addedId, setAddedId] = useState<string | null>(null);

  useEffect(() => {
    fetchPrinters();
  }, []);

  const fetchPrinters = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('printers')
        .select('*')
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;
      setPrinters(data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load printers';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (printer: Printer) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.printer.id === printer.id);
      if (existing) {
        return prev.map((item) =>
          item.printer.id === printer.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { printer, quantity: 1 }];
    });
    setAddedId(printer.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  const removeFromCart = (printerId: string) => {
    setCart((prev) => prev.filter((item) => item.printer.id !== printerId));
  };

  const updateQty = (printerId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.printer.id === printerId ? { ...item, quantity: item.quantity + delta } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.printer.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const filtered = activeCategory === 'All'
    ? printers
    : printers.filter((p) => p.category === activeCategory);

  const discount = (p: Printer) =>
    p.original_price
      ? Math.round(((p.original_price - p.price) / p.original_price) * 100)
      : 0;

  return (
    <main style={{ fontFamily: "'Roboto', sans-serif", minHeight: '100vh', background: '#f8fafc' }}>
      {/* ===== NAVBAR ===== */}
      <section style={{ backgroundColor: 'rgb(255, 255, 255)', padding: '16px 0', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '0 54px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '67px' }}>
          <Link href="/hpprintersetup" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="8" fill="rgb(49,107,156)" />
              <path d="M8 10h6a4 4 0 0 1 0 8H8V10zm0 8h10v4H8v-4z" fill="white" />
            </svg>
            <span style={{ fontFamily: '"Open Sans", sans-serif', fontWeight: 800, fontSize: '20px', color: 'rgb(0,51,102)', letterSpacing: '-0.5px' }}>
              Barb Printer<span style={{ color: 'rgb(49,107,156)' }}> Store</span>
            </span>
          </Link>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '38px' }}>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>DeskJet</Link>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>LaserJet</Link>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>Envy</Link>
            <Link href="/hpprintersetup" style={{ color: 'rgb(0, 51, 102)', textDecoration: 'none', fontSize: '16px' }}>OfficeJet</Link>
            <Link href="/buy" style={{ color: 'rgb(255,255,255)', background: 'rgb(49, 107, 156)', textDecoration: 'none', fontSize: '15px', fontWeight: 700, padding: '8px 20px', borderRadius: '8px' }}>Buy Printers</Link>
            {/* Cart Icon */}
            <button
              onClick={() => setCartOpen(true)}
              style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
              aria-label="Open shopping cart"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgb(49,107,156)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {cartCount > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  background: '#dc2626', color: '#fff',
                  fontSize: '11px', fontWeight: 700,
                  width: '18px', height: '18px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1
                }}>
                  {cartCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </section>

      {/* ===== HERO BANNER ===== */}
      <section style={{
        background: 'linear-gradient(135deg, #003366 0%, #0d5fa3 45%, #1a8fd1 100%)',
        padding: '0',
        overflow: 'hidden',
        position: 'relative',
        minHeight: '320px',
        display: 'flex',
        alignItems: 'center',
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '320px', height: '320px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '30%', width: '240px', height: '240px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '20px', left: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '56px 54px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '40px', position: 'relative', zIndex: 1 }}>
          {/* Left: Text content */}
          <div style={{ flex: '1', minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '6px 16px', marginBottom: '20px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              <span style={{ color: '#fbbf24', fontSize: '13px', fontWeight: 700, fontFamily: '"Open Sans", sans-serif', letterSpacing: '0.5px' }}>BEST DEALS ON PRINTERS</span>
            </div>
            <h1 style={{ color: '#fff', fontFamily: '"Open Sans", sans-serif', fontWeight: 800, fontSize: '44px', marginBottom: '16px', lineHeight: 1.15, margin: '0 0 16px 0' }}>
              Find Your Perfect<br />
              <span style={{ color: '#7dd3fc' }}>Printer</span> Today
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '17px', fontFamily: '"Open Sans", sans-serif', maxWidth: '480px', lineHeight: 1.7, margin: '0 0 28px 0' }}>
              Shop top-rated DeskJet, LaserJet, Envy &amp; OfficeJet printers. Every purchase includes free setup support.
            </p>
            {/* Feature pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {[
                { icon: '🚚', label: 'Free Shipping' },
                { icon: '🛠️', label: 'Free Setup' },
                { icon: '🔒', label: 'Secure Checkout' },
              ].map((f) => (
                <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '8px 14px', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: '16px' }}>{f.icon}</span>
                  <span style={{ color: '#fff', fontSize: '13px', fontWeight: 600, fontFamily: '"Open Sans", sans-serif' }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Printer illustration */}
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'relative' }}>
              {/* Glow effect */}
              <div style={{ position: 'absolute', inset: '-20px', background: 'radial-gradient(circle, rgba(125,211,252,0.25) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
              {/* Printer SVG illustration */}
              <svg width="220" height="180" viewBox="0 0 220 180" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Paper tray bottom */}
                <rect x="40" y="140" width="140" height="18" rx="4" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
                {/* Paper stack */}
                <rect x="55" y="132" width="110" height="12" rx="2" fill="rgba(255,255,255,0.9)"/>
                <rect x="58" y="129" width="104" height="5" rx="1" fill="rgba(255,255,255,0.7)"/>
                {/* Printer body */}
                <rect x="30" y="70" width="160" height="68" rx="10" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5"/>
                {/* Printer top panel */}
                <rect x="30" y="55" width="160" height="22" rx="6" fill="rgba(255,255,255,0.25)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
                {/* Paper output slot */}
                <rect x="55" y="68" width="110" height="6" rx="3" fill="rgba(0,0,0,0.2)"/>
                {/* Control panel */}
                <rect x="130" y="78" width="48" height="28" rx="5" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
                {/* Buttons */}
                <circle cx="142" cy="88" r="5" fill="#7dd3fc"/>
                <circle cx="157" cy="88" r="5" fill="#34d399"/>
                <circle cx="168" cy="96" r="4" fill="rgba(255,255,255,0.4)"/>
                {/* Screen/display */}
                <rect x="50" y="78" width="70" height="28" rx="4" fill="rgba(0,30,60,0.4)" stroke="rgba(125,211,252,0.5)" strokeWidth="1"/>
                <rect x="55" y="83" width="40" height="4" rx="2" fill="rgba(125,211,252,0.7)"/>
                <rect x="55" y="91" width="28" height="3" rx="1.5" fill="rgba(255,255,255,0.3)"/>
                {/* Ink cartridge indicator */}
                <rect x="55" y="97" width="10" height="3" rx="1.5" fill="#7dd3fc"/>
                <rect x="68" y="97" width="10" height="3" rx="1.5" fill="#f472b6"/>
                <rect x="81" y="97" width="10" height="3" rx="1.5" fill="#fbbf24"/>
                {/* WiFi symbol */}
                <path d="M100 40 Q110 32 120 40" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <path d="M95 35 Q110 24 125 35" stroke="rgba(255,255,255,0.4)" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <circle cx="110" cy="44" r="3" fill="rgba(255,255,255,0.8)"/>
                {/* Shine */}
                <rect x="35" y="58" width="60" height="4" rx="2" fill="rgba(255,255,255,0.15)"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CATEGORY FILTER ===== */}
      <section style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0' }}>
        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '0 54px', display: 'flex', gap: '4px', overflowX: 'auto' }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '16px 24px',
                border: 'none',
                borderBottom: activeCategory === cat ? '3px solid rgb(49, 107, 156)' : '3px solid transparent',
                background: 'none',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: activeCategory === cat ? 700 : 400,
                color: activeCategory === cat ? 'rgb(49, 107, 156)' : '#64748b',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
                fontFamily: "'Roboto', sans-serif"
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* ===== PRODUCT GRID ===== */}
      <section style={{ maxWidth: '1224px', margin: '0 auto', padding: '40px 54px 60px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#64748b', fontSize: '18px' }}>
            <div style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTopColor: 'rgb(49,107,156)', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
            Loading printers...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#dc2626', fontSize: '16px' }}>
            <p style={{ marginBottom: '16px' }}>{error}</p>
            <button onClick={fetchPrinters} style={{ background: 'rgb(49,107,156)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '15px' }}>
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8', fontSize: '18px' }}>
            No printers found in this category.
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '28px' }}>
            {filtered.map((printer) => (
              <div
                key={printer.id}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  overflow: 'hidden',
                  border: '1px solid #e8edf3',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(49,107,156,0.15)';
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)';
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                }}
              >
                {/* Image */}
                <div style={{ position: 'relative', background: '#f1f5f9', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                  {printer.badge && (
                    <span style={{
                      position: 'absolute', top: '14px', left: '14px',
                      background: 'rgb(49, 107, 156)', color: '#fff',
                      fontSize: '11px', fontWeight: 700, padding: '4px 10px',
                      borderRadius: '20px', letterSpacing: '0.5px', textTransform: 'uppercase'
                    }}>
                      {printer.badge}
                    </span>
                  )}
                  {discount(printer) > 0 && (
                    <span style={{
                      position: 'absolute', top: '14px', right: '14px',
                      background: '#dc2626', color: '#fff',
                      fontSize: '11px', fontWeight: 700, padding: '4px 10px',
                      borderRadius: '20px'
                    }}>
                      -{discount(printer)}%
                    </span>
                  )}
                  <Image
                    src={getPrinterImage(printer)}
                    alt={`${printer.name} - HP ${printer.category} printer`}
                    width={200}
                    height={160}
                    style={{ objectFit: 'contain', maxHeight: '160px' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                    }}
                  />
                </div>

                {/* Details */}
                <div style={{ padding: '20px 22px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgb(49,107,156)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px', display: 'block' }}>
                    {printer.category}
                  </span>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px', lineHeight: 1.3 }}>
                    {printer.name}
                  </h3>
                  <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.6, margin: '0 0 14px', flex: 1 }}>
                    {printer.description.length > 100 ? printer.description.slice(0, 100) + '…' : printer.description}
                  </p>

                  {/* Features */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {printer.features?.slice(0, 3).map((f, i) => (
                      <li key={i} style={{ fontSize: '12.5px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span> {f}
                      </li>
                    ))}
                  </ul>

                  {/* Price */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
                      ${printer.price.toFixed(2)}
                    </span>
                    {printer.original_price && (
                      <span style={{ fontSize: '15px', color: '#94a3b8', textDecoration: 'line-through' }}>
                        ${printer.original_price.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {/* Actions — Setup button removed, only View Details + Add to Cart */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setSelectedPrinter(printer)}
                      style={{
                        flex: 1,
                        background: '#fff',
                        color: 'rgb(49, 107, 156)',
                        border: '2px solid rgb(49, 107, 156)',
                        padding: '11px 0',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontFamily: "'Roboto', sans-serif"
                      }}
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => addToCart(printer)}
                      style={{
                        flex: 1,
                        background: addedId === printer.id ? '#16a34a' : 'rgb(49, 107, 156)',
                        color: '#fff',
                        border: 'none',
                        padding: '11px 0',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontFamily: "'Roboto', sans-serif",
                        transition: 'background 0.2s'
                      }}
                    >
                      {addedId === printer.id ? '✓ Added' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ===== DETAIL MODAL ===== */}
      {selectedPrinter && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setSelectedPrinter(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', maxWidth: '760px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ background: 'rgb(49, 107, 156)', padding: '24px 28px', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>{selectedPrinter.category}</span>
                <h2 style={{ color: '#fff', margin: '4px 0 0', fontSize: '22px', fontWeight: 700 }}>{selectedPrinter.name}</h2>
              </div>
              <button onClick={() => setSelectedPrinter(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px' }}>
              {/* Left: Image */}
              <div style={{ background: '#f1f5f9', borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Image
                  src={getPrinterImage(selectedPrinter)}
                  alt={`${selectedPrinter.name} - HP ${selectedPrinter.category} printer detailed view`}
                  width={240}
                  height={200}
                  style={{ objectFit: 'contain' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                  }}
                />
              </div>

              {/* Right: Info */}
              <div>
                <p style={{ color: '#475569', fontSize: '14px', lineHeight: 1.7, marginBottom: '16px' }}>{selectedPrinter.description}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <span style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a' }}>${selectedPrinter.price.toFixed(2)}</span>
                  {selectedPrinter.original_price && (
                    <span style={{ fontSize: '16px', color: '#94a3b8', textDecoration: 'line-through' }}>${selectedPrinter.original_price.toFixed(2)}</span>
                  )}
                  {discount(selectedPrinter) > 0 && (
                    <span style={{ background: '#fef2f2', color: '#dc2626', fontSize: '13px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px' }}>Save {discount(selectedPrinter)}%</span>
                  )}
                </div>

                {/* Features */}
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontWeight: 700, fontSize: '13px', color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key Features</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {selectedPrinter.features?.map((f, i) => (
                      <li key={i} style={{ fontSize: '13.5px', color: '#475569', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <span style={{ color: '#16a34a', fontWeight: 700, marginTop: '1px' }}>✓</span> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Specs */}
            {selectedPrinter.specs && Object.keys(selectedPrinter.specs).length > 0 && (
              <div style={{ padding: '0 28px 20px' }}>
                <p style={{ fontWeight: 700, fontSize: '13px', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Specifications</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {Object.entries(selectedPrinter.specs).map(([key, val]) => (
                    <div key={key} style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 14px' }}>
                      <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'capitalize', display: 'block', marginBottom: '2px' }}>{key.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div style={{ padding: '20px 28px 28px', display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { addToCart(selectedPrinter); setSelectedPrinter(null); setCartOpen(true); }}
                style={{
                  flex: 1,
                  background: 'rgb(49, 107, 156)',
                  color: '#fff',
                  border: 'none',
                  padding: '14px 0',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '15px',
                  cursor: 'pointer',
                  fontFamily: "'Roboto', sans-serif"
                }}
              >
                Add to Cart
              </button>
              <button
                onClick={() => setSelectedPrinter(null)}
                style={{
                  flex: 1,
                  background: '#fff',
                  color: '#64748b',
                  border: '2px solid #e2e8f0',
                  padding: '14px 0',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  fontFamily: "'Roboto', sans-serif"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SHOPPING CART SIDEBAR ===== */}
      {cartOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}
          onClick={() => setCartOpen(false)}
        >
          <div
            style={{ background: '#fff', width: '100%', maxWidth: '420px', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.18)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cart Header */}
            <div style={{ background: 'rgb(49, 107, 156)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '18px' }}>Shopping Cart</span>
                {cartCount > 0 && (
                  <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: '13px', fontWeight: 700, padding: '2px 10px', borderRadius: '20px' }}>
                    {cartCount} item{cartCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <button onClick={() => setCartOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: '34px', height: '34px', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {/* Cart Items */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block' }}>
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  <p style={{ fontSize: '16px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Your cart is empty</p>
                  <p style={{ fontSize: '14px', color: '#94a3b8' }}>Add printers to get started</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {cart.map((item) => (
                    <div key={item.printer.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px', display: 'flex', gap: '12px', alignItems: 'flex-start', border: '1px solid #e2e8f0' }}>
                      <div style={{ background: '#fff', borderRadius: '8px', padding: '8px', flexShrink: 0 }}>
                        <Image
                          src={getPrinterImage(item.printer)}
                          alt={item.printer.name}
                          width={64}
                          height={52}
                          style={{ objectFit: 'contain' }}
                          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px', lineHeight: 1.3 }}>{item.printer.name}</p>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 10px' }}>{item.printer.category}</p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          {/* Qty controls */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              onClick={() => updateQty(item.printer.id, -1)}
                              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', fontWeight: 700 }}
                            >−</button>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', minWidth: '20px', textAlign: 'center' }}>{item.quantity}</span>
                            <button
                              onClick={() => updateQty(item.printer.id, 1)}
                              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', fontWeight: 700 }}
                            >+</button>
                          </div>
                          <span style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
                            ${(item.printer.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.printer.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px', flexShrink: 0, fontSize: '18px', lineHeight: 1 }}
                        aria-label="Remove item"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <span style={{ fontSize: '16px', color: '#64748b', fontWeight: 600 }}>Total</span>
                  <span style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>${cartTotal.toFixed(2)}</span>
                </div>
                <button
                  style={{
                    width: '100%',
                    background: 'rgb(49, 107, 156)',
                    color: '#fff',
                    border: 'none',
                    padding: '14px 0',
                    borderRadius: '10px',
                    fontWeight: 700,
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontFamily: "'Roboto', sans-serif",
                    marginBottom: '10px'
                  }}
                >
                  Checkout — ${cartTotal.toFixed(2)}
                </button>
                <button
                  onClick={() => setCartOpen(false)}
                  style={{
                    width: '100%',
                    background: '#fff',
                    color: '#64748b',
                    border: '2px solid #e2e8f0',
                    padding: '12px 0',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '15px',
                    cursor: 'pointer',
                    fontFamily: "'Roboto', sans-serif"
                  }}
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== POLICIES SECTION ===== */}
      <section style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '48px 0' }}>
        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '0 54px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', marginBottom: '40px' }}>
            {[
              { icon: '🚚', title: 'Free Shipping', desc: 'On all orders over $49. Fast delivery to your door.' },
              { icon: '🛠️', title: 'Free Setup Support', desc: 'Every purchase includes complimentary printer setup assistance.' },
              { icon: '🔒', title: 'Secure Payments', desc: 'Your payment data is encrypted and never stored on our servers.' },
              { icon: '↩️', title: '30-Day Returns', desc: 'Not satisfied? Return within 30 days for a full refund.' },
            ].map((item) => (
              <div key={item.title} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '14px', padding: '24px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: '28px', marginBottom: '12px' }}>{item.icon}</div>
                <h4 style={{ color: '#fff', fontSize: '16px', fontWeight: 700, margin: '0 0 8px' }}>{item.title}</h4>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13.5px', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '20px 24px', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '32px' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12.5px', lineHeight: 1.7, margin: 0 }}>
              <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Disclaimer:</strong> We are not affiliated with or authorized by HP Inc. or any other printer manufacturer. Brand names are used for descriptive purposes only. Our services are independent third-party technical assistance offerings.
            </p>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{ background: '#0f172a', padding: '0' }}>
        {/* Main footer */}
        <div style={{ maxWidth: '1224px', margin: '0 auto', padding: '48px 54px 32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '40px', marginBottom: '40px' }}>
            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="8" fill="rgb(49,107,156)" />
                  <path d="M8 10h6a4 4 0 0 1 0 8H8V10zm0 8h10v4H8v-4z" fill="white" />
                </svg>
                <span style={{ fontWeight: 800, fontSize: '20px', color: '#fff' }}>
                  Barb <span style={{ color: '#7dd3fc' }}>Printer Store</span>
                </span>
              </div>
              <p style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.7, margin: '0 0 20px', maxWidth: '260px' }}>
                Your trusted source for HP printers with free setup support and fast shipping on every order.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                {['📘', '🐦', '📸'].map((icon, i) => (
                  <div key={i} style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', cursor: 'pointer' }}>
                    {icon}
                  </div>
                ))}
              </div>
            </div>

            {/* Shop */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Shop</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {['DeskJet', 'LaserJet', 'Envy', 'OfficeJet'].map((cat) => (
                  <a key={cat} href="#" onClick={(e) => { e.preventDefault(); }} style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px', transition: 'color 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                  >{cat}</a>
                ))}
              </div>
            </div>

            {/* Support */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Support</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Link href="/contact-us" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >Contact Us</Link>
                <Link href="/refund-policy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >Refund Policy</Link>
                <Link href="/hpprintersetup" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >Printer Setup</Link>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Legal</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Link href="/privacy-policy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >Privacy Policy</Link>
                <Link href="/terms-and-condition" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >Terms of Service</Link>
                <Link href="/refund-policy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#7dd3fc')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >Return Policy</Link>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <span style={{ color: '#475569', fontSize: '13px' }}>© 2026 Barb Printer Store. All Rights Reserved. Not affiliated with HP Inc.</span>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <Link href="/privacy-policy" style={{ color: '#475569', textDecoration: 'none', fontSize: '13px' }}>Privacy</Link>
              <Link href="/terms-and-condition" style={{ color: '#475569', textDecoration: 'none', fontSize: '13px' }}>Terms</Link>
              <Link href="/refund-policy" style={{ color: '#475569', textDecoration: 'none', fontSize: '13px' }}>Refunds</Link>
              <Link href="/contact-us" style={{ color: '#475569', textDecoration: 'none', fontSize: '13px' }}>Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
}
